// TODO(api): Real backend integration. Currently scaffolded only.
// Maps the server error envelope { error: { code, message, fields, meta } } to
// ProItemDesignApiError. Activated by setting VITE_USE_MOCK_API=false; see
// pro-item-design.adapter.ts.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  ProItemDesignApiError,
  type CreateDomainInput,
  type CreateItemInput,
  type CreateSubAreaInput,
  type ProDomain,
  type ProItem,
  type ProItemHeader,
  type ProItemDesignApi,
  type ProItemDesignApiErrorCode,
  type ProSubArea,
  type ReorderInput,
  type SaveItemInput,
  type UpdateDomainInput,
  type UpdateSubAreaInput,
} from '../types/pro-item-design.types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

function mapError(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  const payload = candidate.response?.data?.error;
  throw new ProItemDesignApiError(
    (payload?.code as ProItemDesignApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
    { fields: payload?.fields, meta: payload?.meta },
  );
}

// BE emits target='CAREGIVER' for guardian-targeted questions, but the FE
// contract (api-contracts/pro-item-design.md §2.6) uses 'GUARDIAN'. Normalize at
// the ingest boundary so the rest of the FE stays on the 'PATIENT' | 'GUARDIAN'
// union — otherwise guardian rows lose their badge color + i18n label (QC PID-027).
// Remove once the BE emits 'GUARDIAN' directly.
function normalizeItem(item: ProItem): ProItem {
  const linked = item?.linkedQuestions;
  if (!linked?.length) return item;
  let changed = false;
  const linkedQuestions = linked.map((q) => {
    if ((q.target as string) === 'CAREGIVER') {
      changed = true;
      return { ...q, target: 'GUARDIAN' as const };
    }
    return q;
  });
  return changed ? { ...item, linkedQuestions } : item;
}

export const proItemDesignHttpApi: ProItemDesignApi = {
  async getTree(): Promise<ProDomain[]> {
    try {
      const res = await httpClient.get<ApiEnvelope<ProDomain[]>>(
        endpoints.proItemDesign.tree,
      );
      const data = res.data?.data;
      if (!Array.isArray(data)) {
        throw new ProItemDesignApiError('UNKNOWN', 'PRO 항목 트리를 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async getItem(itemId: number): Promise<ProItem> {
    try {
      const res = await httpClient.get<ApiEnvelope<ProItem>>(
        `${endpoints.proItemDesign.items}/${itemId}`,
      );
      return normalizeItem(res.data.data);
    } catch (e) {
      mapError(e);
    }
  },

  async createDomain(input: CreateDomainInput): Promise<ProDomain> {
    try {
      const res = await httpClient.post<ApiEnvelope<ProDomain>>(
        endpoints.proItemDesign.domains,
        input,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async updateDomain(id: number, input: UpdateDomainInput): Promise<ProDomain> {
    try {
      const res = await httpClient.patch<ApiEnvelope<ProDomain>>(
        `${endpoints.proItemDesign.domains}/${id}`,
        input,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async deleteDomain(id: number): Promise<void> {
    try {
      await httpClient.delete(`${endpoints.proItemDesign.domains}/${id}`);
    } catch (e) {
      mapError(e);
    }
  },

  async createSubArea(domainId: number, input: CreateSubAreaInput): Promise<ProSubArea> {
    try {
      const res = await httpClient.post<ApiEnvelope<ProSubArea>>(
        endpoints.proItemDesign.domainSubAreas(domainId),
        input,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async updateSubArea(id: number, input: UpdateSubAreaInput): Promise<ProSubArea> {
    try {
      const res = await httpClient.patch<ApiEnvelope<ProSubArea>>(
        `${endpoints.proItemDesign.subAreas}/${id}`,
        input,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async deleteSubArea(id: number): Promise<void> {
    try {
      await httpClient.delete(`${endpoints.proItemDesign.subAreas}/${id}`);
    } catch (e) {
      mapError(e);
    }
  },

  async createItem(subAreaId: number, input: CreateItemInput): Promise<ProItemHeader> {
    try {
      // Returns a header node only — no linkedQuestions to normalize; the caller
      // re-fetches the full detail via getItem.
      const res = await httpClient.post<ApiEnvelope<ProItemHeader>>(
        endpoints.proItemDesign.subAreaItems(subAreaId),
        input,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async saveDraftItem(id: number, input: SaveItemInput): Promise<ProItem> {
    try {
      const res = await httpClient.patch<ApiEnvelope<ProItem>>(
        `${endpoints.proItemDesign.items}/${id}`,
        { ...input, status: 'DRAFT' },
      );
      return normalizeItem(res.data.data);
    } catch (e) {
      mapError(e);
    }
  },

  async publishItem(id: number, input: SaveItemInput): Promise<ProItem> {
    try {
      const res = await httpClient.put<ApiEnvelope<ProItem>>(
        endpoints.proItemDesign.itemPublish(id),
        input,
      );
      return normalizeItem(res.data.data);
    } catch (e) {
      mapError(e);
    }
  },

  async deleteItem(id: number): Promise<void> {
    try {
      await httpClient.delete(`${endpoints.proItemDesign.items}/${id}`);
    } catch (e) {
      mapError(e);
    }
  },

  async reorder(input: ReorderInput): Promise<void> {
    try {
      await httpClient.post(endpoints.proItemDesign.reorder, input);
    } catch (e) {
      mapError(e);
    }
  },
};
